"use client";
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  CsSlice,
  setFilters,
  setPage,
  setRowsPerPage,
  DeleteCustomersAccountSlice,
  UpdateCustomersAccountSlice,
} from "../redux/slices/allcus";
import { toast } from "react-toastify";
import LazyTable from "./widgets/lazytable";
import moment from "moment";
import { keyframes } from "@emotion/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Close, Refresh as RefreshIcon } from "@mui/icons-material";

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const AllCustomers = () => {
  const { page, data, rowsPerPage, totalUsers, loading, filters } = useSelector(
    (v) => v?.allcus
  );
  const currentuser = useSelector((v) => v?.login?.user);
  const dispatch = useDispatch();
  const [dialup, setDialup] = useState({
    open: false,
    previousData: [],
  });
  const [dialupR, setDialupR] = useState({
    open: false,
    previousData: [],
  });
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    password: yup
      .string()
      .optional() // ✅ make field optional
      .test(
        "password-length",
        "Password must be at least 6 characters",
        (value) => {
          if (!value) return true; // ✅ skip if empty
          return value.length >= 6;
        }
      ),
    balance: yup
      .number()
      .typeError("Balance must be a number")
      .min(0, "Minimum balance is 0"),
  });

  useEffect(() => {
    dispatch(CsSlice(page, rowsPerPage, filters));
  }, [page, rowsPerPage, filters]);

  const handleEdit = (rowData, index) => {
    setDialup({
      open: true,
      previousData: rowData,
    });
  };

  const handleClose = () => {
    setDialup({
      open: false,
      previousData: [],
    });
  };

  const handleDelete = (rowData, index) => {
    setDialupR({
      open: true,
      previousData: rowData,
    });
  };

  const handleDeleteAccount = async () => {
    const accountId = { id: dialupR?.previousData?._id };
    if (!accountId) {
      console.error("No account ID provided for deletion.");
      return;
    }
    if (deletingId) return;
    setDeletingId(accountId);
    await dispatch(DeleteCustomersAccountSlice(accountId));

    setDialupR({
      open: false,
      previousData: [],
    });
    setDeletingId(null);
  };

  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(event.target.value, 10));
    dispatch(setPage(0));
  };

  const handleRefreshAll = async () => {
    try {
      dispatch(CsSlice(page, rowsPerPage, filters));
    } catch (error) {
      toast.error("Failed to refresh games");
      console.error("Refresh error:", error);
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: dialup?.previousData?.name || "",
      password: "",
      balance: dialup?.previousData?.balance || "",
    },
  });

  // Reset form when dialog opens
  React.useEffect(() => {
    if (dialup?.open) {
      reset({
        name: dialup.previousData?.name || "",
        password: "",
        balance: dialup.previousData?.balance || "",
      });
    }
  }, [dialup, reset]);

  const onSubmit = async (data) => {
    if (updatingId) return;
    setUpdatingId(dialup?.previousData?._id);
    await dispatch(
      UpdateCustomersAccountSlice({
        id: dialup?.previousData?._id,
        ...data,
        uid: currentuser?.id,
        aname: currentuser?.name,
      })
    );

    setDialup({
      open: false,
      previousData: [],
    });
    handleClose();
    setUpdatingId(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          my: 4,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5">
          All Customers {totalUsers}
          <IconButton
            onClick={handleRefreshAll}
            disabled={loading}
            sx={{
              backgroundColor: "#3f51b5",
              ml: 2,
              color: "white",
              "&:hover": {
                backgroundColor: "#303f9f",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
              },
              animation: loading ? `${rotate} 1s linear infinite` : "none",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Typography>
      </Box>

      <LazyTable
        data={data}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={totalUsers}
        loading={loading}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onFilterChange={(filters) => {
          dispatch(setFilters(filters));
        }}
        columns={[
          { field: "name", headerName: "Name", filter: true },
          { field: "email", headerName: "Email", filter: true },
          { field: "balance", headerName: "Wallet Balance", filter: true },
          { field: "role", headerName: "Role", filter: true },
          { field: "ip", headerName: "IP Address", filter: true },
          {
            field: "createdAt",
            headerName: "Created At",
            filter: true,
            render: (rowData) => {
              if (rowData.createdAt) {
                const date = moment(rowData.createdAt);
                if (date.isValid()) {
                  return date.format("MMM DD, YYYY hh:mm A");
                }
              }
              return null;
            },
          },
          {
            field: "actions",
            headerName: "Actions",
            render: (rowData) => (
              <Box display="flex" justifyContent="center" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={updatingId === rowData._id}
                  loading={updatingId === rowData._id}
                  onClick={() => handleEdit(rowData)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  disabled={deletingId === rowData._id}
                  loading={deletingId === rowData._id}
                  onClick={() => handleDelete(rowData)}
                >
                  Delete
                </Button>
              </Box>
            ),
          },
        ]}
      />

      {/*
        <Tables rowData={user}>
          <Column
            header="#"
            body={(rowData, options) => options.rowIndex + 1}
            filter
            filterPlaceholder="Search by SrNo"
          />
          <Column
            field="name"
            header="Name"
            body={(rowData, options) => <span>{rowData?.name}</span>}
            filter
            filterPlaceholder="Search by Name"
          />
          <Column
          field="email"
          header="Email"
          body={(rowData, options) => <span>{rowData?.user?.email}</span>}
          filter
          filterPlaceholder="Search by Email"
        />
          <Column
            field="balance"
            header="Wallet Balance"
            body={(rowData, options) => <span>{`$${rowData?.balance}`}</span>}
            filter
            filterPlaceholder="Search by Email"
          />
          <Column
            field="role"
            header="Role"
            body={(rowData, options) => <span>{rowData?.role}</span>}
            filter
            filterPlaceholder="Search by Email"
          />
        </Tables>
      */}
      <Dialog
        open={dialupR?.open}
        onClose={() => setDialupR({ open: false, previousData: [] })}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, flexGrow: 1, p: 0 }}>
            Delete User
          </DialogTitle>
          <IconButton
            onClick={() => setDialupR({ open: false, previousData: [] })}
            disabled={deletingId}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ width: 420, pt: 1 }}>
          <Typography>Are you sure you want to remove this account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deletingId}
            onClick={() => setDialupR({ open: false, previousData: [] })}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            disabled={deletingId}
            loading={deletingId}
            color="secondary"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialup?.open}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <DialogTitle sx={{ fontWeight: 600, flexGrow: 1, p: 0 }}>
            Edit User
          </DialogTitle>
          <IconButton onClick={handleClose} disabled={updatingId}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ width: 420, pt: 1 }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Name"
                  fullWidth
                  margin="normal"
                  {...field}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Password"
                  fullWidth
                  margin="normal"
                  type="password"
                  {...field}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />
              )}
            />

            <Controller
              name="balance"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Balance"
                  fullWidth
                  margin="normal"
                  type="number"
                  {...field}
                  error={!!errors.balance}
                  helperText={errors.balance?.message}
                />
              )}
            />

            <DialogActions sx={{ px: 0, pt: 2 }}>
              <Button
                onClick={handleClose}
                disabled={updatingId}
                color="primary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="secondary"
                disabled={updatingId}
                loading={updatingId}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllCustomers;
